import React, { useEffect, useMemo, useState } from 'react';
import { HbrSpinner, HbrPagination, HbrDomTable } from '@harbor/elements-react-standalone';
import './DataGridTile.scss';

interface DataGridTileProps {
  columns: any[];
  rows: any[];
  saveColumnWidths?: (columns: any[]) => void;
  setError?: (error: string | null) => void;
  initialized?: boolean;
}

type SortOrder = 'asc' | 'desc' | null;

type NormalizedColumn = {
  id: string;
  name: string;
  type: 'string' | 'number' | 'date';
  sortable: boolean;
};

type NormalizedRow = {
  id: string;
  values: Record<string, string>;
};

const PAGE_OPTIONS = [10, 20, 30];

const inferType = (value: any): 'string' | 'number' | 'date' => {
  if (value == null) return 'string';
  const s = String(value).trim();
  if (!s) return 'string';
  if (!Number.isNaN(Number(s))) return 'number';
  const d = Date.parse(s);
  if (!Number.isNaN(d)) return 'date';
  return 'string';
};

const DataGridTile: React.FC<DataGridTileProps> = ({
                                                     columns = [],
                                                     rows = [],
                                                     initialized = true,
                                                     setError,
                                                   }) => {
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);

  const [sortCol, setSortCol] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>(null);

  const normalized = useMemo(() => {
    try {
      const safeCols = Array.isArray(columns) ? columns : [];
      const safeRows = Array.isArray(rows) ? rows : [];

      const cols: NormalizedColumn[] = safeCols.map((c: any, idx: number) => {
        const id = String(c?.prop ?? c?.key ?? c?.field ?? `col_${idx}`);
        const name = String(c?.label ?? c?.name ?? c?.title ?? c?.prop ?? `Column ${idx + 1}`);

        const sampleRow = safeRows.find((r: any) => r?.[id] != null && String(r[id]).trim() !== '');
        const sample = sampleRow?.[id];
        const type = inferType(sample);

        return {
          id,
          name,
          type,
          sortable: true,
        };
      });

      const data: NormalizedRow[] = safeRows.map((r: any, i: number) => {
        const id =
          r?.id != null
            ? String(r.id)
            : r?.uuid != null
              ? String(r.uuid)
              : `row-${i}`;

        const values: Record<string, string> = {};
        cols.forEach((col) => {
          const raw = r?.[col.id];
          if (raw == null || String(raw).trim() === '') values[col.id] = '—';
          else if (typeof raw === 'object') values[col.id] = JSON.stringify(raw);
          else values[col.id] = String(raw);
        });

        return { id, values };
      });

      return { cols, data };
    } catch (e: any) {
      setError?.(`DataGrid parse error: ${e?.message || String(e)}`);
      return { cols: [] as NormalizedColumn[], data: [] as NormalizedRow[] };
    }
  }, [columns, rows, setError]);

  useEffect(() => {
    if (!PAGE_OPTIONS.includes(pageSize)) setPageSize(10);
  }, [pageSize]);

  useEffect(() => {
    setError?.(null);
  }, [setError, normalized.cols.length, normalized.data.length]);

  // Reset sort if selected column no longer exists
  useEffect(() => {
    if (sortCol && !normalized.cols.some((c) => c.id === sortCol)) {
      setSortCol(null);
      setSortOrder(null);
      setPage(0);
    }
  }, [normalized.cols, sortCol]);

  const sortedData = useMemo(() => {
    if (!sortCol || !sortOrder) return normalized.data;

    const sorted = [...normalized.data].sort((a, b) => {
      const av = a.values[sortCol] ?? '';
      const bv = b.values[sortCol] ?? '';

      const an = Number(av);
      const bn = Number(bv);
      const bothNumeric = !Number.isNaN(an) && !Number.isNaN(bn);

      if (bothNumeric) return sortOrder === 'asc' ? an - bn : bn - an;
      const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
      return sortOrder === 'asc' ? cmp : -cmp;
    });

    return sorted;
  }, [normalized.data, sortCol, sortOrder]);

  const totalRows = sortedData.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));

  useEffect(() => {
    const maxPage = Math.max(0, totalPages - 1);
    if (page > maxPage) setPage(maxPage);
  }, [page, totalPages]);

  const pagedRows = useMemo(() => {
    const start = page * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, page, pageSize]);

  const tableData = useMemo(
    () =>
      pagedRows.map((r) => {
        const out: any = { id: r.id };
        normalized.cols.forEach((c) => {
          out[c.id] = r.values[c.id];
        });
        return out;
      }),
    [pagedRows, normalized.cols]
  );

  return (
    <div className="stb-datagrid-pro">
      <div className="stb-datagrid-pro__content">
        {!initialized ? (
          <div className="stb-datagrid-pro__state">
            <HbrSpinner size="small">
              <div slot="label">Loading table...</div>
            </HbrSpinner>
          </div>
        ) : normalized.cols.length === 0 ? (
          <div className="stb-datagrid-pro__state">No columns configured.</div>
        ) : totalRows === 0 ? (
          <div className="stb-datagrid-pro__state">No data available.</div>
        ) : (
          <div className="stb-datagrid-pro__scroll">
            <HbrDomTable
              columns={normalized.cols as any}
              data={tableData as any}
              density="compact"
              rowLevel
              autoLayout={true}
              onHbr-sort={(event: any) => {
                const detail = event?.detail || {};
                const col = detail?.column?.id ?? null;
                const order = detail?.order ?? null;
                setSortCol(col);
                setSortOrder(order === 'asc' || order === 'desc' ? order : null);
                setPage(0);
              }}
            >
              {tableData.map((row) =>
                normalized.cols.map((col) => (
                  <div key={`cell-${row.id}-${col.id}`} slot={`row-${row.id}-column-${col.id}`}>
                    {row[col.id] ?? '—'}
                  </div>
                ))
              )}
            </HbrDomTable>
          </div>
        )}
      </div>

      {initialized && totalRows > 0 && (
        <div className="stb-datagrid-pro__pagination">
          <HbrPagination
            basic
            sizeChanger
            pageInfo
            page={page}
            pageSize={pageSize}
            itemCount={totalRows}
            pageOptions={PAGE_OPTIONS as any}
            orderArray={[2, 1, 0] as any}
            onHbr-page-changed={(e: any) => {
              const next = Number(e?.detail?.page ?? e?.detail?.value ?? e?.target?.page ?? 0);
              if (!Number.isNaN(next) && next >= 0) setPage(next);
            }}
            onHbr-size-changed={(e: any) => {
              const nextSize = Number(e?.detail?.pageSize ?? e?.detail?.value ?? e?.target?.pageSize ?? pageSize);
              if (!Number.isNaN(nextSize) && nextSize > 0) {
                setPageSize(nextSize);
                setPage(0);
              }
            }}
          />
        </div>
      )}
    </div>
  );
};

export default DataGridTile;